import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import defaultAvatar from "../../../assets-user/agents/default_avatar.png";
import verifiedImg from "../../../assets-user/agents/VerifiedBadge.png";
import api from "../../../configs/api";
import { cleanArray } from "../../../utils/helpers";
const AgentProfile = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await api.get(`/agent-builder/${id}`);
        const data = res.data?.profile;
        if (!data) return;

        const transformed = {
          id: data._id,
          operatingSince: data.operatingSince,
          operatingAreas: cleanArray(data.operatingAreas) || [],
          dealTypes: cleanArray(data.dealTypes) || [],
          reraId: data.reraId,
          propertiesForSale: data.propertiesForSale || 0,
          dealClosed: data.dealClosed || 0,
          teamMembers: data.teamMembers || 0,

          // If works independently or company attached
          worksWithCompany: Boolean(data.companyId),
          company: data.companyId
            ? {
                name: data.companyId.companyName,
                logo: data.companyId.companyLogo,
                description: data.companyId.description,
                reraId: data.companyId.reraId,
                newLaunch: data.companyId.newLaunch,
                readyToMove: data.companyId.readyToMove,
                underConstruction: data.companyId.underConstruction,
              }
            : null,

          // User info
          name: data.userId?.fullName || "Unknown",
          verified: Boolean(data.userId?.isVerified),
          image: data.userId?.profileImage || defaultAvatar,
        };

        setAgent(transformed);
      } catch (err) {
        console.error("Failed loading agent:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [id]);

  if (loading) return <AgentDetailsSkeleton />;
  if (!agent) return <p className="text-center py-10">Agent not found.</p>;

  return (
    <div className="py-4 sm:py-6 md:py-8 lg:py-10">
      <div className="bg-[#F4E3D9]">
        <div className="mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
            {/* LEFT */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10">
                {/* Profile Header */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 md:gap-6">
                  {/* Left Image */}
                  <div className="flex flex-col items-center sm:items-start gap-2">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden">
                      <img
                        src={agent.image}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {agent.verified && (
                      <img
                        src={verifiedImg}
                        alt=""
                        className="w-24 sm:w-28 md:w-32"
                      />
                    )}
                  </div>

                  {/* Profile Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <h1 className="font-[Poppins] font-medium text-[20px] sm:text-[24px] md:text-[28px] lg:text-[32px] tracking-[0.01em] text-[#031722] mb-2">
                      {agent.name}
                    </h1>

                    {agent.company ? (
                      <p className="font-[Poppins] text-[14px] sm:text-[15px] md:text-[16px] text-[#222]">
                        {agent.company.name}
                      </p>
                    ) : (
                      <p className="font-[Poppins] text-[14px] sm:text-[15px] md:text-[16px] text-[#222]">
                        Independent Agent / Property Consultant
                      </p>
                    )}

                    <div className="space-y-1 text-xs sm:text-sm mt-2">
                      <p className="text-gray-700">
                        <span className="font-medium text-[#000750]">
                          RERA ID:
                        </span>{" "}
                        <span className="text-[#707070]">{agent.reraId}</span>
                      </p>

                      <p className="text-gray-700">
                        <span className="font-medium text-[#000750]">
                          Operating Since:
                        </span>{" "}
                        <span className="text-[#707070]">
                          {agent.operatingSince}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Company Section */}
                {agent.company && (
                  <div className="px-0 sm:px-4 md:px-10 lg:px-0 lg:pl-36 md:pl-36 mt-4 md:-mt-8">
                    <div className="bg-[#FFF4F4] rounded-xl p-6 shadow-md">
                      <div className="flex flex-col md:flex-row items-start gap-4">
                        <div className="border rounded-lg">
                          <img
                            src={agent.company.logo}
                            alt=""
                            className="w-24 h-24 rounded-lg"
                          />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-[#031722] font-medium text-[20px] mb-1">
                            {agent.company.name}
                          </h3>
                          <p className="text-[#7F7F7F] font-normal text-[14px] mb-4">
                            {agent.company.description}
                          </p>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <StatBox
                              label="Property for Sale"
                              value={agent.propertiesForSale}
                            />
                            <StatBox
                              label="Deal Closed"
                              value={agent.dealClosed}
                            />
                            <StatBox
                              label="Team Members"
                              value={agent.teamMembers}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <button className="mt-4 lg:mt-6 bg-red-500 hover:bg-red-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md">
                      Contact Agent
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="space-y-4 px-4 sm:px-6 md:px-10 lg:pr-10 lg:mt-12 xl:mt-20 mb-4">
              {/* Operating In */}
              <div className="bg-[#FFF7F7] rounded-2xl p-4 sm:p-5 md:p-6 line-clamp-6">
                <h3 className="text-[#222] font-medium text-[16px]">
                  Operating In:
                </h3>
                <div className="text-[#818181] flex flex-wrap text-[12px] mt-2">
                  {agent.operatingAreas.join(", ")}
                </div>
              </div>

              {/* Deals In */}
              <div className="bg-[#FFF7F7] rounded-2xl p-4 sm:p-5 md:p-6">
                <h3 className="text-[#222] font-medium text-[16px]">
                  Deals In:
                </h3>
                <ol className="text-[#818181] text-[12px] mt-2 space-y-1 line-clamp-6">
                  {agent.dealTypes.map((d, i) => (
                    <li key={i}>
                      {i + 1}. {d}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }) => (
  <div>
    <div className="text-[#031722] text-[23px] font-medium">{value}</div>
    <div className="text-[#969696] text-[13px]">{label}</div>
  </div>
);

const AgentDetailsSkeleton = () => {
  return (
    <div className="animate-pulse py-8">
      <div className="bg-[#F4E3D9] p-6 rounded-2xl">
        {/* Profile Header */}
        <div className="flex gap-6">
          <div className="flex flex-col items-center">
            <div className="w-28 h-28 bg-gray-300 rounded-full" />
            <div className="w-24 h-6 bg-gray-200 mt-2 rounded" />
          </div>

          <div className="flex-1 space-y-3">
            <div className="h-6 w-40 bg-gray-300 rounded" />
            <div className="h-4 w-32 bg-gray-200 rounded" />
            <div className="h-3 w-24 bg-gray-200 rounded" />
          </div>
        </div>

        {/* Company Card */}
        <div className="mt-10 bg-[#FFF4F4] p-6 rounded-xl">
          <div className="flex gap-6">
            <div className="w-32 h-28 bg-gray-300 rounded" />
            <div className="flex-1 space-y-3">
              <div className="h-5 w-32 bg-gray-300 rounded" />
              <div className="h-4 w-48 bg-gray-200 rounded" />

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="h-5 bg-gray-200 rounded" />
                <div className="h-5 bg-gray-200 rounded" />
                <div className="h-5 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        </div>

        {/* Right cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
          <div className="bg-white p-4 rounded-xl space-y-3">
            <div className="h-5 w-24 bg-gray-300 rounded" />
            <div className="h-3 w-full bg-gray-200 rounded" />
          </div>

          <div className="bg-white p-4 rounded-xl space-y-3">
            <div className="h-5 w-24 bg-gray-300 rounded" />
            <div className="h-3 w-full bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfile;

// import creativeBuilderImg from "../../../assets-user/creativeBuilder.png";
// import agentImg from "../../../assets-user/userBg.png";
// import verifiedImg from "../../../assets-user/agents/VerifiedBadge.png";

// const AgentProfile = () => {
//   const operatingLocations = [
//     "Khordagiri",
//     "Nayapalli",
//     "Pahala",
//     "Tamando",
//     "Kalinga Nagar",
//     "Raghunathpur",
//     "Sundarpur",
//     "Jatani",
//     "Patrapada",
//     "Gothapatna",
//     "Aiginia",
//     "Jagamara",
//     "Sijua",
//     "Patia",
//     "Dumuduma",
//     "BJB Nagar",
//   ];

//   const deals = [
//     { id: 1, text: "Pre-launch" },
//     { id: 2, text: "Original Booking" },
//     { id: 3, text: "Resale" },
//   ];

//   return (
//     <div className="py-4 sm:py-6 md:py-8 lg:py-10">
//       <div className="bg-[#F4E3D9]">
//         <div className="mx-auto">
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
//             {/* Left Column - Profile Section */}
//             <div className="lg:col-span-2">
//               <div className="rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10">
//                 <div
//                   className="flex flex-col sm:flex-row gap-4 sm:gap-5 md:gap-6"
//                   data-aos="fade-right"
//                   data-aos-duration="800"
//                 >
//                   {/* Profile Image */}
//                   <div className="flex flex-col items-center sm:items-start gap-2">
//                     <div
//                       className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden"
//                       data-aos="zoom-in"
//                       data-aos-delay="200"
//                     >
//                       <img
//                         src={agentImg}
//                         alt="Sushant Lenka"
//                         className="w-full h-full object-cover"
//                       />
//                     </div>
//                     <div className="" data-aos="fade-up" data-aos-delay="300">
//                       <img
//                         src={verifiedImg}
//                         alt=""
//                         className="w-24 sm:w-28 md:w-32"
//                       />
//                     </div>
//                   </div>

//                   {/* Profile Info */}
//                   <div className="flex-1 text-center sm:text-left">
//                     <h1
//                       className="font-[Poppins] font-medium text-[20px] sm:text-[24px] md:text-[28px] lg:text-[32px] tracking-[0.01em] text-[#031722] mb-2"
//                       data-aos="fade-down"
//                       data-aos-delay="100"
//                     >
//                       Sushant Lenka
//                     </h1>
//                     <p
//                       className="font-[Poppins] font-medium text-[14px] sm:text-[15px] md:text-[16px] tracking-[0.01em] text-[#222222]"
//                       data-aos="fade-up"
//                       data-aos-delay="200"
//                     >
//                       Creative Builders & Property Consultant
//                     </p>
//                     <div
//                       className="space-y-1 text-xs sm:text-sm"
//                       data-aos="fade-up"
//                       data-aos-delay="300"
//                     >
//                       <p className="text-gray-700">
//                         <span className="font-[Poppins] font-medium text-[14px] sm:text-[15px] md:text-[16px] tracking-[0.01em] text-[#000750]">
//                           RERA ID:
//                         </span>{" "}
//                         <span className="text-[#707070]">ORA/0145/2022</span>
//                       </p>
//                       <p className="text-gray-700">
//                         <span className="font-[Poppins] font-medium text-[14px] sm:text-[15px] md:text-[16px] tracking-[0.01em] text-[#000750]">
//                           Operating Since:
//                         </span>{" "}
//                         <span className="text-[#707070]">2010</span>
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Company Info Card */}
//                 <div className="px-0 sm:px-4 md:px-10 lg:px-0 lg:pl-36 md:pl-36 mt-4 md:-mt-12">
//                   <div
//                     className="bg-[#FFF4F4] rounded-xl p-6 shadow-md"
//                     data-aos="fade-up"
//                     data-aos-delay="400"
//                   >
//                     <div className="flex flex-col md:flex-row items-start gap-4">
//                       <div
//                         className="border rounded-lg"
//                         data-aos="flip-left"
//                         data-aos-delay="500"
//                       >
//                         <img
//                           src={creativeBuilderImg}
//                           alt=""
//                           className="w-full"
//                         />
//                       </div>
//                       <div className="flex-1">
//                         <h3
//                           className="text-[#031722] font-poppins font-medium text-[20px] tracking-[1%] mb-1"
//                           data-aos="fade-left"
//                           data-aos-delay="600"
//                         >
//                           Creative Builders
//                         </h3>
//                         <p
//                           className="text-[#7F7F7F] font-poppins font-normal text-[14px] tracking-[1%] mb-4"
//                           data-aos="fade-left"
//                           data-aos-delay="700"
//                         >
//                           A leading Realtor in Bhubaneswar, Odisha
//                         </p>

//                         {/* Stats */}
//                         <div
//                           className="grid grid-cols-3 gap-4 mt-4"
//                           data-aos="fade-up"
//                           data-aos-delay="800"
//                         >
//                           <div className="">
//                             <div className="text-[#031722] font-poppins font-medium text-[23.48px] tracking-[1%]">
//                               50
//                             </div>
//                             <div className="text-[#969696] font-poppins font-medium text-[13.04px] tracking-[1%] mt-1">
//                               Property for Sale
//                             </div>
//                           </div>
//                           <div className="">
//                             <div className="text-[#031722] font-poppins font-medium text-[23.48px] tracking-[1%]">
//                               50
//                             </div>
//                             <div className="text-[#969696] font-poppins font-medium text-[13.04px] tracking-[1%] mt-1">
//                               Deal Closed
//                             </div>
//                           </div>
//                           <div className="">
//                             <div className="text-[#031722] font-poppins font-medium text-[23.48px] tracking-[1%]">
//                               10
//                             </div>
//                             <div className="text-[#969696] font-poppins font-medium text-[13.04px] tracking-[1%] mt-1">
//                               Team Members
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Contact Button */}
//                   <button
//                     className="mt-4 bg-red-500 hover:bg-red-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md"
//                     data-aos="zoom-in"
//                     data-aos-delay="900"
//                   >
//                     Contact Agent
//                   </button>
//                 </div>
//               </div>
//             </div>

//             {/* Right Column - Operating In & Deals */}
//             <div className="space-y-4 px-4 sm:px-6 md:px-10 lg:pr-10 lg:mt-12 xl:mt-20 mb-4">
//               {/* Operating In Card */}
//               <div
//                 className="bg-[#FFF7F7] rounded-2xl p-4 sm:p-5 md:p-6"
//                 data-aos="fade-left"
//                 data-aos-delay="200"
//               >
//                 <h3 className="text-[#222222] font-poppins font-medium text-[16px] tracking-[1%]">
//                   Operating In:
//                 </h3>
//                 <div className="text-[#818181] flex flex-wrap font-poppins font-medium text-[12px] tracking-[1%] mt-2">
//                   {operatingLocations.map((location, index) => (
//                     <span key={index}>
//                       {location}
//                       {index < operatingLocations.length - 1 && ", "}
//                     </span>
//                   ))}
//                 </div>
//               </div>

//               {/* Deals In Card */}
//               <div
//                 className="bg-[#FFF7F7] rounded-2xl p-4 sm:p-5 md:p-6"
//                 data-aos="fade-left"
//                 data-aos-delay="400"
//               >
//                 <h3 className="text-[#222222] font-poppins font-medium text-[16px] tracking-[1%]">
//                   Deals in:
//                 </h3>
//                 <ol className="text-[#818181] font-poppins font-medium text-[12px] tracking-[1%] mt-2">
//                   {deals.map((deal) => (
//                     <li key={deal.id}>
//                       {deal.id}. {deal.text}
//                     </li>
//                   ))}
//                 </ol>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgentProfile;
